import type {JiraObject, WorkstreamMetric} from 'client';
import {formatCompactMetricValue} from 'helpers/compactMetricValue';
import {formatShortDate, parseUtcDateInput} from 'helpers/dates';
import {getPlainTextTitle} from 'views/workstreams/detail/workstreamManageOutcomesUtils';
import {
  getOutcomeStatus,
  isTerminalOutcomeStatus,
} from 'views/workstreams/detail/WorkstreamOutcomeStatus';
import {
  getCurrentMetricsPeriod,
  getOutcomeMetaParts,
  isCancelledJiraOutcome,
  isCompletedOutcome,
} from 'views/workstreams/detail/WorkstreamRedesignSections';
import {getPrimaryNorthStarMetric, isClosedMetricStatus} from 'views/workstreams/utils';

/**
 * /workstreams: the open card's Progress box, built from the
 * workstream's metrics, milestones, and Jira initiatives in one row shape: the
 * north star pinned, the rest grouped by type, attention first.
 */
export type ProgressItem = {
  key: string;
  kind: 'metric' | 'milestone' | 'jira';
  title: string;
  // A StatusButtonIcon status.
  status: string;
  closed: boolean;
  // Off track, at risk / behind, or overdue (and still open).
  attention: boolean;
  // Most urgent first: off track, at risk, then overdue.
  attentionRank: number;
  dueTime: number;
  dueLabel?: string;
  overdue: boolean;
  // A closed milestone's "Completed Oct 14" / "Missed Oct 14", shown in place
  // of its due date, and when that was (NaN when unknown or still open).
  closedLabel?: string;
  closedTime: number;
  ownerName: string;
  metricTypeLabel?: string;
  jiraKey?: string;
  // Against the current quarter's plan (see currentQuarterLabel), the same for
  // every metric on the card, so only "budget" / "target" varies.
  value?: {current: string; plan?: string; planLabel: 'budget' | 'target'};
  isNorthStar: boolean;
};

const OFF_TRACK_RANK = 0;
const AT_RISK_RANK = 1;
const OVERDUE_RANK = 2;
const NO_ATTENTION_RANK = 3;

const rankFor = (status: string, overdue: boolean) =>
  status === 'off_track'
    ? OFF_TRACK_RANK
    : status === 'at_risk'
      ? AT_RISK_RANK
      : overdue
        ? OVERDUE_RANK
        : NO_ATTENTION_RANK;

const dueTimeOf = (value?: string | null) => {
  const time = value ? new Date(value).getTime() : NaN;
  return Number.isNaN(time) ? Infinity : time;
};

const metricStatus = (status?: string | null) => {
  switch (status) {
    case 'behind':
      return 'off_track';
    case 'at_risk':
      return 'at_risk';
    case 'on_track':
      return 'on_track';
    case 'closed':
      return 'closed';
    default:
      return 'no_status';
  }
};

// Jira status names vary in spacing ("At  Risk"), so match them normalized.
const jiraStatus = (item: JiraObject) => {
  if (isCancelledJiraOutcome(item)) return 'cancelled';
  switch ((item.status ?? '').split(/\s+/).filter(Boolean).join(' ').toLowerCase()) {
    case 'on track':
      return 'on_track';
    case 'at risk':
      return 'at_risk';
    case 'delayed':
    case 'deprioritized':
      return 'off_track';
    case 'done':
    case 'completed':
      return 'complete';
    default:
      return 'no_status';
  }
};

// "Q4": the quarter every metric value is measured against, said once per label.
export const currentQuarterLabel = () => `Q${getCurrentMetricsPeriod().slice(-1)}`;

const METRIC_TYPE_LABELS: Record<string, string> = {leading: 'Leading', lagging: 'Lagging'};

const metricValue = (metric: WorkstreamMetric): ProgressItem['value'] => {
  const period = getCurrentMetricsPeriod();
  const forecast = metric.metric_forecasts?.find((entry) => entry.period === period);
  const current = metric.current?.trim() ?? '';
  const plan = forecast?.budget?.trim() || forecast?.target?.trim() || '';
  if (!current && !plan) return undefined;
  return {
    current: current ? formatCompactMetricValue(current).displayValue : '-',
    plan: plan ? formatCompactMetricValue(plan).displayValue : undefined,
    planLabel: forecast?.budget?.trim() ? 'budget' : 'target',
  };
};

const fromMetric = (
  metric: WorkstreamMetric,
  northStarId: number | undefined,
  fallbackOwner?: {name?: string | null}
): ProgressItem => {
  const isMetric = metric.type === 'metric';
  const meta = getOutcomeMetaParts(metric, fallbackOwner);
  if (isMetric) {
    const status = metricStatus(metric.status);
    const closed = isClosedMetricStatus(metric.status);
    const attentionRank = closed ? NO_ATTENTION_RANK : rankFor(status, false);
    return {
      key: `metric-${metric.id}`,
      kind: 'metric',
      title: getPlainTextTitle(metric.title),
      status,
      closed,
      attention: attentionRank !== NO_ATTENTION_RANK,
      attentionRank,
      dueTime: Infinity,
      overdue: false,
      closedTime: NaN,
      ownerName: meta.ownerName,
      metricTypeLabel: METRIC_TYPE_LABELS[metric.metric_type ?? ''],
      value: metricValue(metric),
      isNorthStar: metric.id === northStarId,
    };
  }
  const outcome = getOutcomeStatus(metric);
  const closed = isTerminalOutcomeStatus(outcome);
  const overdue = !closed && meta.isOverdue;
  // Stamped by the server whenever a milestone is completed or missed.
  const closedTime =
    closed && metric.completed_at ? parseUtcDateInput(metric.completed_at).getTime() : NaN;
  return {
    key: `metric-${metric.id}`,
    kind: 'milestone',
    title: getPlainTextTitle(metric.title),
    status: outcome === 'completed' ? 'complete' : outcome === 'missed' ? 'missed' : 'not_started',
    closed,
    attention: overdue,
    attentionRank: overdue ? OVERDUE_RANK : NO_ATTENTION_RANK,
    dueTime: dueTimeOf(metric.complete_date),
    dueLabel: meta.dueLabel,
    overdue,
    closedLabel: Number.isNaN(closedTime)
      ? undefined
      : `${outcome === 'missed' ? 'Missed' : 'Completed'} ${formatShortDate(closedTime)}`,
    closedTime,
    ownerName: meta.ownerName,
    isNorthStar: false,
  };
};

/** The starred north star as the open card pins it, for a collapsed card's line. */
export const getNorthStarItem = (
  workstreamId: number,
  metrics: WorkstreamMetric[]
): ProgressItem | undefined => {
  const northStar = getPrimaryNorthStarMetric(metrics, workstreamId);
  return northStar ? fromMetric(northStar, northStar.id) : undefined;
};

const fromJira = (item: JiraObject, fallbackOwner?: {name?: string | null}): ProgressItem => {
  const meta = getOutcomeMetaParts(item, fallbackOwner);
  const closed = isCompletedOutcome(item);
  const status = jiraStatus(item);
  const overdue = !closed && meta.isOverdue;
  const attentionRank = closed ? NO_ATTENTION_RANK : rankFor(status, overdue);
  return {
    key: `jira-${item.id}-${item.key}`,
    kind: 'jira',
    title: getPlainTextTitle(item.title),
    status,
    closed,
    attention: attentionRank !== NO_ATTENTION_RANK,
    attentionRank,
    dueTime: dueTimeOf(item.complete_date),
    dueLabel: meta.dueLabel,
    overdue,
    // Jira syncs no close time.
    closedTime: NaN,
    ownerName: meta.ownerName,
    jiraKey: item.key,
    isNorthStar: false,
  };
};

const byUrgency = (a: ProgressItem, b: ProgressItem) =>
  a.attentionRank - b.attentionRank || a.dueTime - b.dueTime || a.title.localeCompare(b.title);

export type ScanProgress = {
  // The north star: the one row a reader sees before opening the rest.
  pinned?: ProgressItem;
  // The top set expects a north star; this one has none.
  missingNorthStar: boolean;
  // Folded below the pinned row, by type, each attention first, then soonest
  // due. Open items only, but for milestones closed since `closedSince`.
  metrics: ProgressItem[];
  milestones: ProgressItem[];
  // Jira roadmap initiatives: milestones too, but kept apart so it's clear
  // where they come from.
  initiatives: ProgressItem[];
};

export const buildScanProgress = ({
  workstreamId,
  metrics,
  jiraObjects,
  isCore,
  fallbackOwner,
  closedSince = Infinity,
}: {
  workstreamId: number;
  metrics: WorkstreamMetric[];
  jiraObjects: JiraObject[];
  // In the top set (pillar workstream or level 2).
  isCore: boolean;
  fallbackOwner?: {name?: string | null};
  // Milestones closed at or after this time stay listed, as news; every other
  // closed item is left to the detail page. (Metrics can no longer close, and
  // Jira initiatives have no close time.)
  closedSince?: number;
}): ScanProgress => {
  const northStar = getPrimaryNorthStarMetric(metrics, workstreamId);
  const items = [
    ...metrics.map((metric) => fromMetric(metric, northStar?.id, fallbackOwner)),
    ...jiraObjects.map((item) => fromJira(item, fallbackOwner)),
  ];
  // Only a starred north star is pinned. Without one, the card opens its
  // metrics & milestones instead of promoting a metric nobody chose.
  const pinned = items.find((item) => item.isNorthStar);

  const rest = items.filter((item) => item !== pinned);
  const open = rest.filter((item) => !item.closed).sort(byUrgency);
  const openMilestones = open.filter((item) => item.kind === 'milestone');
  // Newest first, after the milestones that need attention.
  const recentlyClosed = rest
    .filter((item) => item.kind === 'milestone' && item.closed && item.closedTime >= closedSince)
    .sort((a, b) => b.closedTime - a.closedTime);

  return {
    pinned,
    missingNorthStar: isCore && !northStar,
    metrics: open.filter((item) => item.kind === 'metric'),
    milestones: [
      ...openMilestones.filter((item) => item.attention),
      ...recentlyClosed,
      ...openMilestones.filter((item) => !item.attention),
    ],
    initiatives: open.filter((item) => item.kind === 'jira'),
  };
};
